// shiftScope.js
// The ONE place that answers "which shift is this device/guest talking to?"
//
// WHY THIS EXISTS (2026-09-25, load-bearing):
// Until now the whole system assumed exactly one open shift globally —
// 16 call sites ran `where("isActive","==",true)` and took `docs[0]` off an
// UNORDERED query. That is a coin flip the moment two events run at once
// (Dan at one booth, Wella at another), and it can flip between snapshots.
//
// THE RULE: a shift IS the event's queue context. There is no separate
// "station" concept — multiple artists share one shift's queue, and multiple
// shifts run concurrently for multiple events.
//
// RESOLUTION ORDER (see resolveShift below):
//   0 active shifts  → null
//   1 active shift   → THAT ONE, unconditionally. Bindings and params are
//                      ignored. This is what keeps every ordinary solo market
//                      byte-identical to pre-2026-09-25 behavior — the new
//                      machinery is inert until a second queue actually opens.
//   2+ active shifts → URL param (?s=CODE / ?shift=ID) → device binding
//                      (localStorage) → needsChoice, and the caller prompts.
//
// Never guess when it is ambiguous. A wrong guess puts a guest in the wrong
// queue at the wrong venue, which is worse than one extra tap.

// ── Device binding ───────────────────────────────────────────────────────────
// Per-browser-profile sticky. Deliberately NOT derived from auth: Dan and
// Wella share an admin account today, and shipping a Firestore rules rewrite
// days before two live events is the one failure mode worse than a confused
// queue. When per-artist logins land, THIS is the function that changes —
// every caller already goes through the hook, so nothing else moves.
const BIND_ID_KEY    = "gt_bound_shift_id";
const BIND_LABEL_KEY = "gt_bound_shift_label";

export function getBoundShiftId() {
  try { return localStorage.getItem(BIND_ID_KEY) || null; } catch { return null; }
}

export function getBoundShiftLabel() {
  try { return localStorage.getItem(BIND_LABEL_KEY) || null; } catch { return null; }
}

export function setBoundShift(shiftId, label) {
  try {
    if (!shiftId) return clearBoundShift();
    localStorage.setItem(BIND_ID_KEY, String(shiftId));
    if (label) localStorage.setItem(BIND_LABEL_KEY, String(label));
  } catch { /* private mode / storage disabled — resolver falls back to prompt */ }
}

export function clearBoundShift() {
  try {
    localStorage.removeItem(BIND_ID_KEY);
    localStorage.removeItem(BIND_LABEL_KEY);
  } catch { /* no-op */ }
}

// ── URL scoping (OBS browser sources + QR deep links) ────────────────────────
// `?s=CODE`   — join code, what the on-screen QR and typed fallback use.
// `?shift=ID` — raw doc id, for staff deep links where the code isn't handy.
// An OBS browser source URL is the most reliable binding we have: it is set
// once per rig and survives cache clears, unlike localStorage.
export function readShiftParam(search) {
  try {
    const p = new URLSearchParams(search ?? window.location.search);
    const code = normalizeJoinCode(p.get("s") || p.get("code") || "");
    const id   = (p.get("shift") || p.get("shiftId") || "").trim();
    return { code: code || null, id: id || null };
  } catch {
    return { code: null, id: null };
  }
}

// ── Join codes ───────────────────────────────────────────────────────────────
// Guests may TYPE this off the booth screen, so the alphabet excludes I and O
// (confusable with 1 and 0) and digits entirely. 24^4 = 331,776 — far more
// than needed, since a code only has to be unique among CURRENTLY ACTIVE
// shifts plus whatever stale QRs are still in the wild.
const CODE_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ";

// Regenerate rather than ship a code that reads badly on a banner screen.
// Substring match, so this also catches the code sitting inside a longer word.
const CODE_BLOCKLIST = ["ASS", "CUM", "FAG", "FUK", "JIZ", "TIT", "SEX", "NAZ", "KKK", "DAM", "WTF"];

export function generateJoinCode(taken = []) {
  const used = new Set((taken || []).filter(Boolean).map(c => String(c).toUpperCase()));
  for (let attempt = 0; attempt < 200; attempt++) {
    let code = "";
    for (let i = 0; i < 4; i++) {
      code += CODE_ALPHABET[Math.floor(Math.random() * CODE_ALPHABET.length)];
    }
    if (used.has(code)) continue;
    if (CODE_BLOCKLIST.some(bad => code.includes(bad))) continue;
    return code;
  }
  // Astronomically unlikely; a duplicate code is still recoverable (the picker
  // disambiguates), an exception during openShift is not.
  return CODE_ALPHABET[Math.floor(Math.random() * 24)] + "XYZ";
}

export function normalizeJoinCode(raw) {
  return String(raw || "").toUpperCase().replace(/[^A-Z]/g, "").slice(0, 4);
}

export function isCompleteJoinCode(raw) {
  return normalizeJoinCode(raw).length === 4;
}

// ── Labels ───────────────────────────────────────────────────────────────────
// What a guest sees in the booth picker and an operator sees in the shift chip.
// Falls back through the shift's own naming before resorting to the code, so a
// picker never shows two indistinguishable rows.
export function shiftLabel(shift) {
  if (!shift) return "";
  return (
    shift.joinLabel
    || shift.eventName
    || shift.venueName
    || (shift.joinCode ? `Booth ${shift.joinCode}` : "Booth")
  );
}

// What a GUEST may be shown in the booth picker.
//
// The picker is reachable from the public linktree, so its labels are public
// text — anyone on the internet can open /join while two booths are live.
// A client-paid private event must not publish the client's name there.
//
// `venueIsPublic` is the field that already means "this event may be named
// publicly" (it gates the venue on the website), so it gates this too rather
// than inventing a second notion of the same thing. A private booth is
// identified by its code, which is printed on its own screen — enough for a
// guest standing in front of it, useless to anyone who isn't.
export function guestSafeLabel(shift) {
  if (!shift) return "";
  if (shift.joinLabel) return shift.joinLabel;      // explicit operator override wins
  if (shift.venueIsPublic === false) {
    return shift.joinCode ? `Private event · ${shift.joinCode}` : "Private event";
  }
  return shiftLabel(shift);
}

// ── The resolver ─────────────────────────────────────────────────────────────
// `shifts` is every shift with isActive == true. Returns the resolved shift
// plus WHY, so callers can render an honest prompt instead of a silent guess.
//
// `needsChoice` is true only when a real ambiguity exists AND we could not
// resolve it — never when there is a single shift, so the prompt cannot
// appear on an ordinary one-queue day.
export function resolveShift(shifts, opts = {}) {
  const list = (shifts || []).filter(Boolean);

  if (list.length === 0) {
    return { shift: null, candidates: [], needsChoice: false, reason: "none" };
  }

  const { code, id } = opts.param || readShiftParam();

  // An explicit ?s= / ?shift= is a STRICT assertion, checked BEFORE the
  // single-shift shortcut below (2026-09-25).
  //
  // A rig's URL says "I am the WELA booth". On the morning of a two-event day
  // the first shift opens alone, and the lenient rule ("one live shift → use
  // it") made the second rig display the FIRST event's queue and QR until its
  // own shift opened — guests at Wella's booth scanning into Dan's queue, at
  // another venue, for a pancake nobody would make. That is the precise
  // failure this module exists to prevent, so a named booth that isn't live
  // resolves to nothing and the screen says so.
  //
  // The cost is that a mistyped or forgotten code no longer silently "works"
  // on a solo day. That's the right trade: a screen reading "not assigned to
  // a booth" is embarrassing and fixable in fifteen seconds; a screen quietly
  // showing the wrong venue is not discovered until someone is owed a pancake.
  if (id || code) {
    const byId   = id   ? list.find(s => s.id === id) : null;
    if (byId)   return { shift: byId,   candidates: list, needsChoice: false, reason: "param-id" };
    const byCode = code ? list.find(s => normalizeJoinCode(s.joinCode) === code) : null;
    if (byCode) return { shift: byCode, candidates: list, needsChoice: false, reason: "param-code" };
    return {
      shift: null, candidates: list, needsChoice: true,
      reason: "param-miss", requested: code || id,
    };
  }

  // No explicit scope. A single active shift wins outright, bindings included:
  // a device bound to yesterday's other booth must still work at today's solo
  // market without anyone remembering to unbind it.
  if (list.length === 1) {
    return { shift: list[0], candidates: list, needsChoice: false, reason: "only" };
  }

  const boundId = opts.boundId !== undefined ? opts.boundId : getBoundShiftId();
  if (boundId) {
    const byBinding = list.find(s => s.id === boundId);
    if (byBinding) return { shift: byBinding, candidates: list, needsChoice: false, reason: "bound" };
  }

  // Last resort, and only when the caller opted in (overlay surfaces pass
  // `defaultCode`). A bare overlay URL means "the usual rig", so an unscoped
  // browser source keeps working when a second, explicitly-scoped rig joins.
  // Staff and guest callers deliberately omit this and get the picker.
  const fallback = normalizeJoinCode(opts.defaultCode || "");
  if (fallback) {
    const byDefault = list.find(s => normalizeJoinCode(s.joinCode) === fallback);
    if (byDefault) {
      return { shift: byDefault, candidates: list, needsChoice: false, reason: "default-station" };
    }
  }

  // Ambiguous and unresolvable. The caller prompts; we do not pick.
  return { shift: null, candidates: list, needsChoice: true, reason: "ambiguous" };
}
